import { ChevronDown } from 'lucide-react-native';
import { twJoin } from 'tailwind-merge';

import { HStack } from '@/components/ui/hstack';
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from '@/components/ui/select';
import { COMMUNITY_SORT_OPTIONS, type SortOption } from '@/constants/community';
import { trackCommunitySortBy } from '@/util/analyticsEvents';

interface CommunitySortByProps {
  value: SortOption;
  className?: string;
  /** `compact` is a borderless text trigger, for the end of a toolbar row. */
  variant?: 'default' | 'compact';
  onChange: (value: SortOption) => void;
}

const paddingBottom = 32;

export function CommunitySortBy({
  value,
  onChange,
  className,
  variant = 'default',
}: CommunitySortByProps) {
  const selectedValue = value || 'newest';

  const selectedLabel =
    COMMUNITY_SORT_OPTIONS.find((opt) => opt.value === selectedValue)?.label || 'Newest';

  const handleSortChange = (val: SortOption) => {
    try {
      onChange(val);

      trackCommunitySortBy({ sortOption: val });
    } catch (error) {
      trackCommunitySortBy({
        sortOption: val,
        error: error instanceof Error ? error.message : JSON.stringify(error),
      });
    }
  };

  const isCompact = variant === 'compact';

  return (
    <Select
      selectedValue={selectedValue}
      onValueChange={(val) => handleSortChange(val as SortOption)}
      aria-label="Sort inquiries by"
      className={twJoin('mb-0 min-h-[44px]', isCompact ? 'w-auto' : 'w-full', className)}>
      <SelectTrigger
        className={twJoin(
          'items-center bg-background-0',
          isCompact
            ? 'h-11 min-h-[44px] w-auto justify-end gap-2 border-0 bg-transparent px-2 data-[focus-visible=true]:web:ring-2 data-[focus-visible=true]:web:ring-indicator-info'
            : 'w-full justify-between p-2'
        )}>
        <SelectInput
          className={twJoin(
            'text-typography-900',
            isCompact && 'flex-none px-0 font-medium web:w-auto'
          )}
          // Sized to the label so the chevron sits beside it; an input has no intrinsic fit.
          style={isCompact ? ({ width: `${selectedLabel.length + 1}ch` } as never) : undefined}
          placeholder="Sort by"
          value={selectedLabel}
        />
        <SelectIcon as={ChevronDown} />
      </SelectTrigger>

      <SelectPortal>
        <SelectBackdrop />
        <SelectContent className={twJoin('w-full', `max-h-[70vh] pb-${paddingBottom}`)}>
          <SelectDragIndicatorWrapper>
            <SelectDragIndicator />
          </SelectDragIndicatorWrapper>
          <SelectScrollView>
            {COMMUNITY_SORT_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} label={opt.label} value={opt.value} />
            ))}
          </SelectScrollView>
        </SelectContent>
      </SelectPortal>
    </Select>
  );
}
